import { Box, Stack } from "@mui/material";
import BusinessRecommendation from "../components/BusinessRecommendation";
import BusinessDataSourcePanel from "../components/BusinessDataSourcePanel";
import StockSummaryGrid from "../components/StockSummaryGrid";
import StockFilterBar from "../components/StockFilterBar";
import { getDomainProfile, CP_WEALTH_NET_DOMAIN } from "../config/domainProfiles";
import { legacySectionSkin } from "../components/liveGridStyles";

/**
 * DEVELOPMENT-ONLY visual harness.
 *
 * Renders the Live tab's bottom sections with fixture data so their appearance can
 * be compared against tools/cpwealth_dashboard/demo/style_demo.html?s=assertion
 * without a logged-in session and a live run. Reasoning about CSS specificity
 * instead of looking at the render is what let three rounds of styling ship
 * without matching the demo.
 *
 * Registered behind a NODE_ENV check in app.js, so it cannot reach production.
 */


/* Enough shape to render the grid: the real payload comes from the report
   document, which needs a session. Values are already-quantised, as the server
   sends them. */
const COLS = [["Stock", "txt"], ["Price Yesterday", "num"], ["CV", "num"], ["MOS", "num"],
  ["HB", "num"], ["PEG", "num"], ["Rev_1yr", "pct"], ["Rev_3yr", "pct"],
  ["Notes", "txt"], ["Industry", "hid"]];
const ROWS = [
  ["A", 139.2, 135, 135.23, 188, 1.92, 0.0512, 0.0992, "", "Precision Instrument"],
  ["AA", 46.83, 78.38, null, 88.13, 1.33, 0.0221, 0.0429, "PEG Ratio below 1.5", "Metals & Mining Diversified"],
  ["AAL", 16.56, 10.8, null, 21.6, 0.37, -0.0076, 0.0191, "PEG Ratio below 1.5", "Air Transport"],
  ["AAON", 95.55, 72.45, null, 133.35, 1.46, 0.1016, 0.1225,
   "PEG Ratio below 1.5; Double digit 1yr. and higher for 3 yr.", "Building Materials"],
  ["AAPL", 309.38, 278.6, 296.82, 414.4, 2.36, 0.1208, 0.1358,
   "Double digit 1yr. and higher for 3 yr.", "Computer Peripherals"],
];
const mk = () => ({
  cols: COLS, rows: ROWS,
  iIdx: 9, sIdx: 0, nIdx: 8,
  show: COLS.map((c, i) => i).filter((i) => COLS[i][1] !== "hid"),
});
const STOCK_DATA = {
  summary: mk(), detail: mk(),
  industries: ["Air Transport", "Building Materials", "Computer Peripherals"],
  isoDate: "2026-08-05",
};

const DECISION = {
  summary:
    "Avoid the worst-performing 20% decisions. Screening the bottom 20% of scored " +
    "trades raises average buy return from 30.65% to 35.80%.",
  uncertainty: {
    description:
      "Baseline is every trade in the reference window; after filtering removes the " +
      "worst-scoring 20%.",
    metrics: [
      { name: "Average Buy Return", baseline: "30.65%", after_filtering: "35.80%", improvement: "+16.8%" },
      { name: "Average Sell Return", baseline: "12.43%", after_filtering: "8.61%", improvement: "+30.7%" },
      { name: "Weighted Buy Return", baseline: "78.03%", after_filtering: "97.82%", improvement: "+25.4%" },
      { name: "Weighted Sell Return", baseline: "11.69%", after_filtering: "9.55%", improvement: "+18.3%" },
      { name: "Buy Profit", baseline: "$4.69M", after_filtering: "$4.71M", improvement: "+0.6%" },
      { name: "Sell Loss", baseline: "-$346.83K", after_filtering: "-$220.92K", improvement: "+36.3%" },
    ],
  },
  drivers: {
    description:
      "The following five features are the most important drivers of the model's " +
      "predictions, based on feature importance ranking.",
    features: [
      "security_momentum_ratio_7d_30d: Compares the short-term price average to the long-term average.",
      "transaction_price_vs_security_30d_avg: Assesses the transaction price against the longer-term average.",
      "transaction_price_vs_security_7d_avg: Evaluates the transaction price against the short-term average.",
      "security_30d_avg_adj_close: Provides a longer-term view of the security's price trend.",
      "transaction_price: Captures the investment's entry price, the denominator in the return calculation.",
    ],
  },
  guardrails: [
    "Market conditions may evolve over time, so results should be interpreted alongside current context.",
    "The model is designed for prioritization rather than highly precise point forecasts.",
    "Performance may vary in situations driven by unusual events or low liquidity.",
    "Applying filters may reduce overall trade volume while improving overall quality.",
    "Some excluded opportunities may still perform well on an individual basis.",
    "Periodic recalibration can help maintain alignment with changing market dynamics",
  ],
  /* Makes the percentile-legend bullets render so they can be compared with the
     Drivers/Risks bullets. The embedded table itself needs a real run, so it shows
     its own error here — the bullets below it are the point. */
  user_live_prediction_display: { bq_table: "user_live_prediction_display" },
  evidence: {
    description: "Backtest over the reference window.",
    metrics: [
      { name: "Trades screened out", value: "20%" },
      { name: "Average buy return after screening", value: "35.80%" },
    ],
  },
};

export default function LivePreview() {
  const domainProfile = getDomainProfile(CP_WEALTH_NET_DOMAIN);
  return (
    <Box sx={{ p: 3, bgcolor: "#F7F9FA", minHeight: "100vh" }}>
      <Stack spacing={3} sx={{ maxWidth: 1220, mx: "auto" }}>
        <StockFilterBar
          industries={STOCK_DATA.industries} stockOptions={["A", "AA", "AAL"]}
          industry="" stock="" onIndustry={() => {}} onStock={() => {}} onClear={() => {}}
        />
        <StockSummaryGrid data={STOCK_DATA} industry="" stock="" vintage="Aug 5, 2026, 1:34 PM" />
        <Box sx={legacySectionSkin}>
          <BusinessDataSourcePanel title="Your trades" runId={null} />
        </Box>
        <Box sx={legacySectionSkin}>
          <BusinessRecommendation
            decisionOutput={DECISION}
            loading={false}
            runId="preview-run"
            domainProfile={domainProfile}
          />
        </Box>
      </Stack>
    </Box>
  );
}
